import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IoRefreshOutline } from "react-icons/io5";
import { BsFillBoxSeamFill, BsCheckCircleFill, BsClockHistory } from "react-icons/bs";
import { HiOutlineCurrencyPound } from "react-icons/hi2";
import Box from "../../../setupPage/SharedComponents/Box";
import useParcelBookingRefresh from "../../../../hooks/useParcelBookingRefresh";
import { fetchAdminBookingAnalytics } from "../../../../api/parcelBookingInformationApi";
import { mapAdminBookingAnalyticsToView } from "../../../../utils/mapAdminBookingAnalytics";
import AnalyticsDonutChart from "./charts/AnalyticsDonutChart";
import AnalyticsProductBarChart from "./charts/AnalyticsProductBarChart";
import AnalyticsCurrencyBarChart from "./charts/AnalyticsCurrencyBarChart";
import "./AdminBookingAnalytics.css";

const AdminBookingAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const data = await fetchAdminBookingAnalytics();
      setAnalyticsData(data);
      setError(null);
    } catch (err) {
      setError(err);
      if (!silent) setAnalyticsData(null);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useParcelBookingRefresh(() => fetchData({ silent: true }));

  const view = useMemo(
    () => mapAdminBookingAnalyticsToView(analyticsData),
    [analyticsData],
  );

  const summary = view.summary;

  const currencyBarData = useMemo(
    () =>
      view.totalsByCurrency.map((c) => ({
        currency: c.currencyCode,
        amount: c.totalAmount,
      })),
    [view.totalsByCurrency],
  );

  if (loading && analyticsData == null) {
    return (
      <Box top>
        <div className="admin-analytics">
          <div className="admin-analytics__shell">
            <div className="admin-analytics__topbar admin-analytics__skel admin-analytics__skel--topbar" />
            <div className="admin-analytics__body">
              <div className="admin-analytics__grid">
                <div className="admin-analytics__skel admin-analytics__skel--kpi" />
                <div className="admin-analytics__skel admin-analytics__skel--kpi" />
                <div className="admin-analytics__skel admin-analytics__skel--kpi" />
                <div className="admin-analytics__skel admin-analytics__skel--kpi" />
                <div className="admin-analytics__skel admin-analytics__skel--chart" />
                <div className="admin-analytics__skel admin-analytics__skel--chart" />
                <div className="admin-analytics__skel admin-analytics__skel--chart-wide" />
              </div>
            </div>
          </div>
        </div>
      </Box>
    );
  }

  if (error && !analyticsData) {
    return (
      <Box top>
        <div className="admin-analytics">
          <div className="admin-analytics__shell">
            <div className="admin-analytics__error">
              Could not load analytics. {error.message}
            </div>
          </div>
        </div>
      </Box>
    );
  }

  return (
    <Box top>
      <div className="admin-analytics">
        <div className="admin-analytics__shell">
          <header className="admin-analytics__topbar">
            <div className="admin-analytics__topbar-left">
              <h1>Booking analytics</h1>
              <p>
                Real-time insights on bookings, payments, product mix, and revenue.
              </p>
            </div>
            <div className="admin-analytics__topbar-actions">
              {view.generatedAt ? (
                <span className="admin-analytics__live">
                  <span className="admin-analytics__live-dot" />
                  Updated {new Date(view.generatedAt).toLocaleString()}
                </span>
              ) : null}
              <button
                type="button"
                className="admin-analytics__btn"
                onClick={fetchData}
                disabled={loading}
              >
                <IoRefreshOutline size={16} />
                Refresh
              </button>
            </div>
          </header>

          <div className="admin-analytics__body">
            {!view.hasData ? (
              <div className="admin-analytics__empty">
                No analytics data available yet.
              </div>
            ) : (
              <div className="admin-analytics__grid">
                <article className="admin-analytics__kpi admin-analytics__kpi--bookings">
                  <div className="admin-analytics__kpi-icon">
                    <BsFillBoxSeamFill />
                  </div>
                  <div>
                    <p className="admin-analytics__kpi-label">Total bookings</p>
                    <p className="admin-analytics__kpi-value">
                      {summary?.totalBookings ?? 0}
                    </p>
                    <p className="admin-analytics__kpi-hint">
                      {summary?.totalLineItems ?? 0} line items
                    </p>
                  </div>
                </article>

                <article className="admin-analytics__kpi admin-analytics__kpi--paid">
                  <div className="admin-analytics__kpi-icon">
                    <BsCheckCircleFill />
                  </div>
                  <div>
                    <p className="admin-analytics__kpi-label">Paid</p>
                    <p className="admin-analytics__kpi-value">
                      {summary?.paidCount ?? 0}
                    </p>
                    <p className="admin-analytics__kpi-hint">
                      {summary?.paidPercent ?? 0}% of parcels
                    </p>
                  </div>
                </article>

                <article className="admin-analytics__kpi admin-analytics__kpi--unpaid">
                  <div className="admin-analytics__kpi-icon">
                    <BsClockHistory />
                  </div>
                  <div>
                    <p className="admin-analytics__kpi-label">Not paid</p>
                    <p className="admin-analytics__kpi-value">
                      {summary?.unpaidCount ?? 0}
                    </p>
                    <p className="admin-analytics__kpi-hint">
                      {summary?.unpaidPercent ?? 0}% awaiting payment
                    </p>
                  </div>
                </article>

                <article className="admin-analytics__kpi admin-analytics__kpi--revenue">
                  <div className="admin-analytics__kpi-icon">
                    <HiOutlineCurrencyPound />
                  </div>
                  <div>
                    <p className="admin-analytics__kpi-label">Primary revenue</p>
                    <p className="admin-analytics__kpi-value admin-analytics__kpi-value--sm">
                      {view.grandTotalFormatted}
                    </p>
                    <p className="admin-analytics__kpi-hint">
                      {summary?.primaryCurrencyCode ?? "—"} ·{" "}
                      {view.totalsByCurrency.length} currencies
                    </p>
                  </div>
                </article>

                <div className="admin-analytics__charts-row">
                  <AnalyticsDonutChart
                    title="Payment status"
                    subtitle="Paid vs not paid parcels"
                    data={view.paymentChartData}
                    centerLabel={summary?.totalBookings ?? 0}
                    emptyMessage="No payment data"
                  />
                  <AnalyticsDonutChart
                    title="Product mix"
                    subtitle="Share by line item count"
                    data={view.productGroupChartData}
                    centerLabel={summary?.totalLineItems ?? 0}
                    emptyMessage="No product group data"
                  />
                </div>

                <div className="admin-analytics__full-row">
                  <AnalyticsProductBarChart
                    dataset={view.barChartDataset}
                    emptyMessage="No product groups to chart"
                  />
                </div>

                <div className="admin-analytics__charts-row admin-analytics__charts-row--currency">
                  <AnalyticsCurrencyBarChart
                    data={currencyBarData}
                    emptyMessage="No currency totals"
                  />
                  <section className="admin-analytics__currency-panel">
                    <div className="aa-chart-card__head">
                      <h3>Currency breakdown</h3>
                      <p>Total value per currency</p>
                    </div>
                    <div className="admin-analytics__currency-list">
                      {view.totalsByCurrency.map((c) => (
                        <div
                          key={c.currencyCode}
                          className="admin-analytics__currency-item"
                        >
                          <span className="admin-analytics__currency-code">
                            {c.currencyCode}
                          </span>
                          <span className="admin-analytics__currency-amount">
                            {c.formatted}
                          </span>
                        </div>
                      ))}
                    </div>
                  </section>
                </div>

                <section className="admin-analytics__table-section">
                  <div className="admin-analytics__table-head">
                    <h3>Detailed breakdown</h3>
                    <span>
                      {view.productGroupRows.length} product groups
                    </span>
                  </div>
                  <div className="admin-analytics__table-scroll">
                    <table className="admin-analytics__table">
                      <thead>
                        <tr>
                          <th>Product group</th>
                          <th className="r">Items</th>
                          <th className="r">Share</th>
                          <th className="r">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {view.productGroupRows.map((row) => (
                          <tr key={row.key}>
                            <td>{row.name}</td>
                            <td className="r">{row.lineItemCount}</td>
                            <td className="r">
                              <span className="admin-analytics__pct">
                                {row.sharePercent}%
                              </span>
                            </td>
                            <td className="r">{row.formattedAmount}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td>Total</td>
                          <td className="r">{summary?.totalLineItems ?? 0}</td>
                          <td className="r">
                            <span className="admin-analytics__pct">100%</span>
                          </td>
                          <td className="r">
                            {view.totalsByCurrency.map((c) => (
                              <div key={c.currencyCode}>{c.formatted}</div>
                            ))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </div>
    </Box>
  );
};

export default AdminBookingAnalytics;
